import React, { useEffect, useRef, useState, useCallback } from "react";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import { getAll } from "../../api/API";
import LoadingSpinner from "../Generic/LoadingSpinner";
import InViewFade from "../Generic/InViewFade";
import "../../styles/Blogs.css";

const BlogList = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(4); // show first 4
  const scrollContainerRef = useRef(null);

  // ---------- Fetch blogs ----------
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const query = {
          depth: 2,
          where: { status: { equals: "published" } },
          sort: "-publishedDate",
        };
        const res = await getAll("blog-posts", query);
        setBlogs(res.docs || []);
      } catch (error) {
        console.error("Failed to fetch blogs:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogs();
  }, []);

  // ---------- Infinite Scroll Logic ----------
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const nearBottom =
      container.scrollTop + container.clientHeight >=
      container.scrollHeight - 50; // 50px from bottom

    if (nearBottom) {
      setVisibleCount((prev) => {
        const nextCount = prev + 4;
        return nextCount <= blogs.length ? nextCount : blogs.length;
      });
    }
  }, [blogs.length]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [handleScroll]);

  // ---------- UI Loading ----------
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <LoadingSpinner size="4rem" text="Loading Blogs..." />
      </div>
    );
  }

  if (!blogs.length) {
    return (
      <Container className="py-5 text-center">
        <h3>No blog posts found</h3>
      </Container>
    );
  }

  const visibleBlogs = blogs.slice(0, visibleCount);

  return (
    <div
      style={{
        backgroundImage: "url('/assets/images/blogbackground.svg')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        minHeight: "100vh",
        paddingTop: "1rem",
        paddingBottom: "2rem",
        color: "white",
      }}
    >
      <Container fluid="md">
        <Row
          className="align-items-center justify-content-center"
          style={{ marginTop: "120px" }}
        >
          {/* LEFT TEXT SECTION */}
          <Col
            lg={6}
            md={10}
            sm={12}
            className="blogs-spacer text-center text-lg-start mb-5"
          >
            <h2 className="fw-bold display-6">
              Women in the Semiconductor Ecosystem
            </h2>
            <p className="lead mt-5">
              is a dynamic digital platform dedicated to empowering and
              elevating women across all areas of the semiconductor industry —
              highlighting innovation, mentorship, and leadership journeys.
            </p>
          </Col>

          {/* RIGHT SIDE - SCROLLABLE BLOGS */}
          <Col lg={6} md={10} sm={12}>
            <div
              ref={scrollContainerRef}
              className="d-flex flex-column gap-3"
              style={{
                maxHeight: "500px",
                overflowY: "auto",
                paddingRight: "10px",
                scrollbarWidth: "thin",
              }}
            >
              {visibleBlogs.map((blog, index) => {
                const thumbnail =
                  blog.videoThumbnail?.url ||
                  blog.featuredImage?.url ||
                  "/images/fallback-thumbnail.jpg";

                return (
                  <InViewFade
                    key={blog.slug}
                    delay={index * 0.1}
                    direction="up"
                  >
                    <Card
                      className="flex-row align-items-center bg-white text-dark border-0 rounded-4 shadow-sm flex-wrap flex-md-nowrap"
                      style={{
                        overflow: "hidden",
                        minHeight: "110px",
                      }}
                    >
                      <Card.Img
                        src={thumbnail}
                        alt={blog.title}
                        loading="lazy"
                        className="m-2"
                        style={{
                          width: "90px",
                          height: "90px",
                          objectFit: "cover",
                          borderRadius: "0.75rem",
                          flexShrink: 0,
                        }}
                      />
                      <Card.Body className="p-2">
                        <Card.Title className="fw-semibold mb-1">
                          {blog.title}
                        </Card.Title>
                        <Card.Text className="small text-muted mb-2">
                          {blog.excerpt ||
                            "Women in the Semiconductor Ecosystem is a dynamic digital platform dedicated to empowering and elevating."}
                        </Card.Text>
                        <Button
                          as={Link}
                          to={`/blogs/${blog.slug}`}
                          variant="link"
                          className="p-0 text-dark fw-semibold text-decoration-none text-primary"
                        >
                          Read Article →
                        </Button>
                      </Card.Body>
                    </Card>
                  </InViewFade>
                );
              })}

              {/* Optional: A small loader indicator when more are loading */}
              {visibleCount < blogs.length && (
                <div className="text-center text-light py-3 small">
                  Loading more...
                </div>
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default BlogList;
